import { type FormikHelpers } from "formik";
import { type FormikValues } from "formik";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { renderTemplateUtil } from "shared/components/template/renderTemplateUtil";
import { sanitizeHtml } from "shared/components/template/sanitize-html";
import { formatUsdString } from "shared/helpers/invoice/formatUsdString";
import { type InvoiceItemView } from "shared/mappers/database/accounting/invoice/invoice-item";
import * as Yup from "yup";
import { useHubCheckoutContext } from "hub/src/pages/checkout/context/HubCheckoutContext";

export interface HubCheckoutOptionalGridProduct {
  productId: number;
  productName: string;
  description?: string;
  notesEnabled?: boolean;
  allowRemove?: boolean;
  hideIfProductIds?: number[];
  notesInstructions?: string;
  totalOptions?: number[];
  buttonClassName?: string;
}

export const HubCheckoutOptionalProductItem = ({
  product,
}: {
  product: HubCheckoutOptionalGridProduct;
}) => {
  const {
    invoice,
    invoiceIsLoading,
    handleUpdateInvoice,
    optionalProductZone,
  } = useHubCheckoutContext();

  const renderedLabelTemplate = renderTemplateUtil({
    template: optionalProductZone?.data?.actionLabel ?? "Donate",
    templateData: { product },
  });

  if (
    product.hideIfProductIds?.some((hiddenId) =>
      invoice?.invoiceItems?.some(
        (item: InvoiceItemView) => item.product?.productId === hiddenId,
      ),
    )
  ) {
    return null;
  }

  const totalOptions = product.totalOptions ?? [];
  const hasSingleOption = totalOptions.length === 1;
  const hasMultipleOptions = totalOptions.length > 1;

  const optionalProductYupValidation = Yup.object({
    amount: !hasSingleOption
      ? Yup.number()
          .typeError("Amount is required")
          .min(1, "Amount must be at least $1.00")
          .required("Amount is required")
      : Yup.number().optional(),
  });

  return (
    <fieldset disabled={invoiceIsLoading}>
      <div
        data-testid={`optional-product-${product.productId}`}
        className="self-start pt-1 px-2 border border-neutral-mid-150 rounded-md"
      >
        <h3 className="text-lg font-bold pb-1">{product.productName}</h3>
        {product.description && (
          <p className="text-sm text-neutral-500">{product.description}</p>
        )}

        <PillarForm
          className="flex flex-col items-center gap-2 w-full"
          yupValidation={optionalProductYupValidation}
          handleSubmit={async (
            formValues: FormikValues,
            formikHelpers?: FormikHelpers<FormikValues>,
          ) => {
            await handleUpdateInvoice({
              invoiceItems: [
                {
                  total: Number(
                    hasSingleOption ? totalOptions[0] : formValues.amount,
                  ),
                  productId: Number(product.productId),
                  additionalInfo: {
                    contentZone: { ...product },
                  },
                },
              ],
            });
            formikHelpers?.resetForm?.();
          }}
        >
          <div className="w-full">
            {hasMultipleOptions && (
              <PillarForm.SelectComponent
                name="amount"
                placeholder="Select Amount"
                testid={`optional-product-select-amount-${product.productId}`}
                queryCallback={async ({ text }) => {
                  const results = totalOptions.filter((option) =>
                    formatUsdString(option)
                      .toString()
                      .includes((text || "").toString().toLowerCase()),
                  );
                  return {
                    results,
                    totalResults: 1,
                    page: 1,
                    pageSize: results.length,
                  };
                }}
                labelCallback={(option) => `${formatUsdString(option)}`}
                valueCallback={(option) => option}
              />
            )}

            {!hasMultipleOptions && !hasSingleOption && (
              <PillarForm.NumberInput
                name="amount"
                placeholder="$0.00"
                min={0}
                testid={`optional-product-custom-amount-input-${product.productId}`}
              />
            )}
          </div>

          <PillarForm.LoadingSubmitButton
            name="customAmount"
            className={`${product.buttonClassName ?? "bg-primary-400 hover:bg-primary-600 w-full "} text-center mb-2 `}
            testid={
              hasSingleOption
                ? `optional-product-${product.productId}-${totalOptions[0]}`
                : `optional-product-${product.productId}-custom`
            }
          >
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  hasSingleOption
                    ? `${renderedLabelTemplate} ${formatUsdString(
                        totalOptions[0],
                      )}`
                    : hasMultipleOptions
                      ? renderedLabelTemplate
                      : `${renderedLabelTemplate} Custom Amount`,
                ),
              }}
            />
          </PillarForm.LoadingSubmitButton>
        </PillarForm>
      </div>
    </fieldset>
  );
};
